import styled from 'styled-components';
import { RhythmEvent, RhythmPattern } from '../../../../../common/src/rhythm_echo.ts';
import {
  Baseline,
  beamCount,
  beamSpacing,
  beamThickness,
  flagCount,
  Glyph,
  glyphInset,
  noteGlyph,
  restGlyph,
  rhythmNotationHeight,
  stemTop,
  stemWidth,
  stemX,
} from './rhythm_glyphs.tsx';
import { eventStartBeats, gridColumn, RhythmRow } from './rhythm_layout.tsx';

export const rhythmNotationTestId = 'rhythm-notation';

// The notation row under the syllable blocks (issue #3473): each event drawn with its real note
// value on a single line, no staff. The row is the same sixteenth grid as the blocks, so each
// event's cell is exactly as wide as its block; within the cell, each note is a fixed-size SVG glyph
// (rhythm_glyphs.tsx) positioned at the onset's fraction of the cell (where its syllable starts)
// plus a small inset, and a beam is a stretched bar from the first stem to the last, so nothing
// needs measuring and glyphs never distort. Everything is currentColor so it follows the text
// colour in both themes.
export const RhythmNotation = ({ pattern }: { pattern: RhythmPattern }) => {
  const starts = eventStartBeats(pattern);
  return (
    <Row data-testid={rhythmNotationTestId}>
      <Baseline />
      {pattern.map((event, eventIndex) => (
        <EventCell
          key={starts[eventIndex]} // the start beat is unique per event
          style={{ gridColumn: gridColumn(starts[eventIndex], event.beats) }}
          data-event={eventIndex}
        >
          <EventNotation event={event} />
        </EventCell>
      ))}
    </Row>
  );
};

const EventNotation = ({ event }: { event: RhythmEvent }) => {
  const { value, count, beamed, rest } = event.notation;
  if (rest) {
    return (
      <Glyph style={{ left: glyphInset }} data-rest={value}>
        {restGlyph(value)}
      </Glyph>
    );
  }
  const offsets = event.onsets.map((onset) => (onset / event.beats) * 100);
  const beams = beamed ? beamCount(value) : 0;
  return (
    <>
      {offsets.map((offset, index) => (
        <Glyph key={offset} style={{ left: `calc(${offset}% + ${glyphInset}px)` }} data-note={value} data-onset={index}>
          {noteGlyph(value, beamed || count > 1 ? 0 : flagCount(value))}
        </Glyph>
      ))}
      {Array.from({ length: beams }, (_beam, index) => (
        <Beam
          key={index}
          style={{
            top: stemTop + index * beamSpacing,
            left: `calc(${offsets[0]}% + ${glyphInset + stemX}px)`,
            right: `calc(${100 - offsets[offsets.length - 1]}% - ${glyphInset + stemX + stemWidth}px)`,
          }}
          data-beam={index}
        />
      ))}
    </>
  );
};

// ---- Styles -------------------------------------------------------------------------------------

const Row = styled(RhythmRow)`
  position: relative;
  height: ${rhythmNotationHeight}px;
  color: var(--color-text);
`;

const EventCell = styled.div`
  position: relative;
  height: ${rhythmNotationHeight}px;
`;

const Beam = styled.div`
  position: absolute;
  height: ${beamThickness}px;
  background-color: currentColor;
`;
